/**
 * Полоса вкладок панели «Чатов», которая листается влево-вправо: вкладок больше, чем влезает в
 * 440 px. По краям — стрелки-указатели, пока есть что листать в эту сторону (клик — на две трети
 * ширины); выбранная вкладка сама прокручивается в видимую часть. Колесо мыши листает вбок.
 */
import { ChevronLeft, ChevronRight } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";

export function TabStrip({ active, label, children, tail }: { active: string; label: string; children: ReactNode; tail?: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLDivElement>(null);
  const [edge, setEdge] = useState({ left: false, right: false });
  const measure = useCallback(() => {
    const el = box.current;
    if (!el) return;
    setEdge({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  }, []);
  const reveal = useCallback(() => {
    box.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ inline: "nearest", block: "nearest" });
  }, []);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    measure();
    // Ширина полосы (окно сузили) и ряда вкладок (появилось число, вкладка «Договор») — выбранную
    // возвращаем в видимую часть.
    const ro = new ResizeObserver(() => {
      reveal();
      measure();
    });
    ro.observe(el);
    if (row.current) ro.observe(row.current);
    return () => ro.disconnect();
  }, [measure, reveal]);
  useEffect(reveal, [active, reveal]);
  const go = (dir: -1 | 1) => {
    const el = box.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.66, behavior: "smooth" });
  };
  const arrow =
    "absolute top-0 z-10 grid h-full w-7 place-items-center text-fg-muted hover:text-fg from-bg from-55% to-transparent";
  return (
    <div className="flex h-11 flex-none items-stretch border-b border-border text-[13.5px]">
      <div className="relative flex min-w-0 flex-1">
        {edge.left && (
          <button type="button" onClick={() => go(-1)} title="Вкладки левее" aria-label="Вкладки левее" className={`${arrow} left-0 bg-gradient-to-r`}>
            <ChevronLeft size={16} />
          </button>
        )}
        <div
          ref={box}
          role="tablist"
          aria-label={label}
          onScroll={measure}
          onWheel={(e) => {
            // Колесо без Shift листает вбок — вертикальной прокрутки у полосы нет.
            if (e.deltaY && !e.deltaX && box.current) box.current.scrollLeft += e.deltaY;
          }}
          className="flex min-w-0 flex-1 scroll-px-2 items-stretch overflow-x-auto px-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          data-annot="вкладки панели"
        >
          <div ref={row} className="flex flex-none items-stretch">
            {children}
          </div>
        </div>
        {edge.right && (
          <button type="button" onClick={() => go(1)} title="Вкладки правее" aria-label="Вкладки правее" className={`${arrow} right-0 bg-gradient-to-l`}>
            <ChevronRight size={16} />
          </button>
        )}
      </div>
      {tail}
    </div>
  );
}
